<script setup>
import { ref, onMounted, nextTick} from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2";

import UpdateUserModal from "@/views/Users/UpdateUser.vue";
import NewUser from "@/views/Users/NewUser.vue";
import DeleteUser from "@/views/auth/domain/delete-user";

import GetUsersFromOrg from "@/views/auth/domain/get-users-from-org";
import { DataTable } from "simple-datatables";

const store = useStore();

const headers = ["ID", "Nombre", "Correo", "Telefono", "Rol"];
const users = ref([]);
const error = ref(null);

const selectedUserCedula = ref(null);
const selectedUserName = ref(null);
const selectedUserEmail = ref(null);
const selectedUserPhone = ref(null);
const selectedUserRol = ref(null);

const loading = ref(true);

const showUserModal = ref(false);
const showNewUserModal = ref(false);

const openNewUserModal = () => {
  showNewUserModal.value = true;
};

if (window.innerWidth <= 768) {
  store.commit("navbarMinimize");
}


//Obtiene los usuarios de la organización
// In your fetchUsers function, ensure the data structure matches what you expect
const fetchUsers = async () => {
  try {
    const response = await GetUsersFromOrg();
    console.log(response);
    


    users.value = response.map(user => ({
      id_usuario: user.id,
      nombre: user.nombre,
      email: user.email,
      role_id: user.role_id,
      telefono: user.telefono
    }));
    
  } catch (err) {
    error.value = "No se pudieron cargar los usuarios.";
  } finally {
    loading.value = false;
  }
};

const handleUserDelete = async (userId) => {  // Changed from userCedula to userId
  try {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "¡Este usuario será eliminado permanentemente!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      await DeleteUser(userId);  // Pass userId instead of userCedula

      Swal.fire(
        'Eliminado!',
        'El usuario ha sido eliminado.',
        'success'
      );

      // Consider using fetchUsers() instead of reload for better UX
      location.reload();

      //await fetchUsers();
    }
  } catch (err) {
    console.error("Error eliminando usuario", err);
    Swal.fire(
      'Error',
      'Hubo un problema al eliminar al usuario. Por favor, intenta de nuevo.',
      'error'
    );
  }
};

onMounted(async () => {
  await fetchUsers();
  await nextTick();

  const tableElement = document.getElementById("users-list");
  if (tableElement) {
    new DataTable(tableElement, {
      searchable: true,
      fixedHeight: true,
      perPage: 15,
      labels: {
        placeholder: "Buscar usuario...",
        perPage: "{select} registros por página",
        noRows: "No se encontraron registros",
        info: "Mostrando {start} al {end} de {rows} registros",
      },
      
    });

    tableElement.addEventListener("click", (event) => {
  if (event.target.classList.contains("edit-action")) {
    const row = event.target.closest("tr");
    if (row) {
      const userId = row.querySelector('[data-cedula]').getAttribute('data-cedula');
      const userName = row.children[1].textContent.trim();
      const userEmail = row.children[2].textContent.trim();
      const userPhone = row.children[3].textContent.trim();
      const userRol = row.querySelector('[data-role-id]').getAttribute('data-role-id');     
      
      // For phone, you might need to add it to your table or get it differently
      
      openModal(userId, userName, userEmail, userPhone, userRol);
    }
  }
  if (event.target.classList.contains("delete-action")) {
    const row = event.target.closest("tr");
    const userId = row.querySelector('[data-cedula]').getAttribute('data-cedula');
    handleUserDelete(userId);
  }
});
  }
});

const openModal = (userId, userName, userEmail, userPhone, userRol) => {
  selectedUserCedula.value = userId;
  selectedUserName.value = userName;
  selectedUserEmail.value = userEmail;
  selectedUserPhone.value = userPhone;
  selectedUserRol.value = userRol;  

  showUserModal.value = true;
};

const closeModal = () => {
  showUserModal.value = false;
  showNewUserModal.value = false;
};

</script>

<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div v-if="loading" class="text-center">
        <p>Cargando...</p>
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>

      <div v-else class="card">
        <div class="p-3 pb-0 card-header">
          <div class="d-flex justify-content-between">
            <h6 class="mb-2"><i class="fas fa-users pe-2"></i>Usuarios</h6>
            <button class="btn btn-dark btn-sm mb-0" @click="openNewUserModal">
              + Nuevo Usuario
            </button>
          </div>
        </div>

        <div class="table-responsive pb-4">
          <table id="users-list" class="table align-items-center mb-0">
            <thead>
              <tr>
                <th v-for="(header, index) in headers" :key="index"
                  :class="`text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ${index !== 0 ? 'ps-2' : ''}`">
                  {{ header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.usuarioId" class="row-hover">
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.id_usuario }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <div class="d-flex align-items-center">
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-0 text-xs">{{ user.nombre }}</h6>
                    </div>
                  </div>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.email }}
                  </p>
                </td>
                <td class="px-2 py-1">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.telefono }}
                  </p>
                </td>
                <td class="px-2 py-1" :data-role-id="user.role_id">
                  <p class="text-xs font-weight-bold mb-0">
                    {{ user.role_id === 1 ? 'Propietario' :
                      user.role_id === 2 ? 'Administrador' :
                      user.role_id === 3 ? 'Instructor' :
                      user.role_id === 4 ? 'Estudiante' : 'Desconocido' }}
                  </p>
                </td>

                <td class="align-middle">
                  <!-- Owner (solid star) -->
                  <div v-if="user.role_id === 1" class="d-flex justify-content-start align-middle">
                    <i class="fas fa-star ps-2"></i>
                  </div>              

                  <!-- Others (edit button if permitted) -->
                  <div v-else>
                    <div class="dropdown-xs w-40 ">
                      <button class="btn btn-link text-primary text-xs dropdown-toggle p-0 mb-0" type="button"
                        id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                        Editar
                      </button>
                      <ul class="dropdown-menu w-20" aria-labelledby="dropdownMenuButton">
                        <li>
                          <a class="dropdown-item edit-action" :data-cedula="user.id_usuario" data-type="user">
                            <i class="fas fa-user-edit pe-2"></i>Editar Datos
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item delete-action text-danger" :data-cedula="user.id_usuario"
                            data-type="user">
                            <i class="fas fa-user-times pe-2"></i>Eliminar Usuario
                          </a>
                        </li>
                      </ul>
                    </div>
                   
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modales -->
    <UpdateUserModal 
      v-if="showUserModal" 
      :id="selectedUserCedula" 
      :name="selectedUserName" 
      :email="selectedUserEmail"
      :telefono="selectedUserPhone" 
      :role_id="selectedUserRol" 
      @close="closeModal" 
    />

    <new-user v-if="showNewUserModal" @close="closeModal" />

  </div>
</template>


<style scoped>
.row-hover {
  transition: background-color 0.3s ease;
}

.row-hover:hover {
  background-color: #f0f4f8;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  margin: 2rem auto;
  color: #f0f4f8;
}
</style>
